/* eslint-disable camelcase */
import { AnyAction } from "redux";

import { searchFlightBase } from "../../lib/constants";
import { getBasicDate } from "../../lib/functions";
import * as actionTypes from "../actions/actionTypes";

const INITIAL_DEPARTURE_DATE_DAYS_TO_ADD = 1;
const INITIAL_RETURN_DATE_DAYS_TO_ADD = 8;

interface SearchFlight {
  departureDate: string;
  returnDate: string;
  origin: string;
  destination: string;
}

interface Room {
  ADT: number;
  CHD: number;
  guests: number;
  CHD_age?: number[];
}

interface SearchHotels {
  rooms: Record<number, Room>;
  paxNationality: string | null;
  closedHotelPropertyError: string | null;
}

interface Query {
  [key: string]: boolean | number | string | string[] | undefined;
  airports_from?: boolean;
  airports_transfers?: boolean;
  airports_to?: boolean;
  sort: string;
}

export type SearchState = {
  toleranceDays?: number;
  typeSearch: string;
  from: string;
  to: string;
  flightClass: string;
  departureDate: string;
  returnDate: string;
  ADT: number;
  INF: number;
  CHD: number;
  YTH: number;
  YCD: number;
  passengers: Record<string, number>;
  searchFormAirports: Record<string, unknown>;
  maxTransfers: number | null;
  preferredAirline: string;
  searchFlights: SearchFlight[];
  query: Query;
  oFlightOffers: Record<string, unknown>;
  aFlightOffers: unknown[];
  actualFlightServices: unknown[];
  mobileOffersSortedFiltered: unknown[];
  flights: unknown[];
  oTransportCompanies: Record<string, unknown>;
  airports: Record<string, unknown>;
  airplanes?: Record<string, unknown>;
  travelPoliciesResults?: Record<string, unknown>;
  maxNumTransfers: number | null;
  airportsFrom: unknown[];
  airportsTo: unknown[];
  airportsTransfers: unknown[];
  highestPrice: number;
  lowestPrice: number;
  highestAlternativeCurrencyPrice: number;
  lowestAlternativeCurrencyPrice: number;
  toggleServiceInfo: boolean;
  searchHotels: SearchHotels;
  loading?: boolean;
  error?: unknown;
  errorCode?: unknown;
  /** True after FLIGHT_SEARCH_SUCCESS or FLIGHT_SEARCH_FAIL; used to avoid showing "no results" during initial load before first API response */
  searchResponseReceived?: boolean;
};

const initState: SearchState = {
  typeSearch: "RETURN",
  from: "",
  to: "",
  flightClass: "ECO",
  departureDate: getBasicDate(INITIAL_DEPARTURE_DATE_DAYS_TO_ADD),
  returnDate: getBasicDate(INITIAL_RETURN_DATE_DAYS_TO_ADD),
  ADT: 0,
  INF: 0,
  CHD: 0,
  YTH: 0,
  YCD: 0,
  passengers: {},
  searchFormAirports: {},
  maxTransfers: null,
  preferredAirline: "all",
  searchFlights: [
    {
      ...searchFlightBase,
      departureDate: getBasicDate(7),
      returnDate: getBasicDate(7 + 7),
      origin: "",
      destination: "",
    },
  ],

  // mobile filters (web filters are in url)
  query: {
    airports_from: false,
    airports_transfers: false,
    airports_to: false,
    sort: "PRICE",
  },

  // results web + mobile
  oFlightOffers: {},
  aFlightOffers: [],
  actualFlightServices: [],
  mobileOffersSortedFiltered: [],
  flights: [],
  oTransportCompanies: {},
  airports: {},
  maxNumTransfers: null,
  airportsFrom: [],
  airportsTo: [],
  airportsTransfers: [],
  highestPrice: 999999999,
  lowestPrice: 0,
  highestAlternativeCurrencyPrice: 999999999,
  lowestAlternativeCurrencyPrice: 0,
  toggleServiceInfo: false,
  searchHotels: {
    rooms: {
      0: { ADT: 0, CHD: 0, guests: 0 },
    },
    paxNationality: null,
    closedHotelPropertyError: null,
  },
};

const flightSearchSuccess = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    ...action.data,
    loading: false,
    error: null,
    errorCode: null,
    searchResponseReceived: true,
  };
};

const flightSearchFail = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    flights: [],
    loading: false,
    searchResponseReceived: true,
    ...(action?.data ?? {}),
  };
};

const searchSetTransportCompanies = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    oTransportCompanies: { ...state.oTransportCompanies, ...action.data },
  };
};

const searchSetSearchFlights = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFlights: (action.data as SearchFlight[]) ?? [],
  };
};

const searchAddSearchFlight = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFlights: state.searchFlights.concat(action.data as SearchFlight),
  };
};

const searchRemoveSearchFlight = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFlights: state.searchFlights.filter((_, i) => i !== action.data.id),
  };
};

const searchUpdateSearchFlightOrigin = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  const searchFlightUpdated = state.searchFlights.map((searchFlight, i) =>
    i === action.data.id
      ? {
          ...searchFlight,
          origin: action.data.payload,
          showCodeOrigin: action.data.showCode,
        }
      : searchFlight
  );

  return {
    ...state,
    searchFlights: searchFlightUpdated,
    searchFormAirports: {
      ...state.searchFormAirports,
      showCodeOrigin: action.data.showCode,
    },
  };
};

const searchUpdateSearchFlightDestination = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  const searchFlightUpdated = state.searchFlights.map((searchFlight, i) =>
    i === action.data.id
      ? {
          ...searchFlight,
          destination: action.data.payload,
          showCodeDestination: action.data.showCode,
        }
      : searchFlight
  );

  return {
    ...state,
    searchFlights: searchFlightUpdated,
    searchFormAirports: {
      ...state.searchFormAirports,
      showCodeDestination: action.data.showCode,
    },
  };
};

const searchUpdateSearchFlightDepartureDate = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFlights: state.searchFlights.map((searchFlight, i) =>
      i === action.data.id
        ? { ...searchFlight, departureDate: action.data.payload }
        : searchFlight
    ),
  };
};

const searchUpdateSearchFlightReturnDate = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFlights: state.searchFlights.map((searchFlight, i) =>
      i === action.data.id
        ? { ...searchFlight, returnDate: action.data.payload }
        : searchFlight
    ),
  };
};

const searchSetAirports = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return { ...state, airports: { ...state.airports, ...action.data } };
};

const searchSetPolicies = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    travelPoliciesResults: { ...state.travelPoliciesResults, ...action.data },
  };
};

const searchSetAirplanes = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return { ...state, airplanes: { ...state.airplanes, ...action.data } };
};

const searchSetAirportFilters = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    maxNumTransfers: action.data.maxNumTransfers,
    airportsFrom: [...action.data.airportsFrom],
    airportsTo: [...action.data.airportsTo],
    airportsTransfers: [...action.data.airportsTransfers],
    highestPrice: action.data.highestPrice,
    lowestPrice: action.data.lowestPrice,
  };
};

const setActualFlightServices = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    actualFlightServices: action.data,
  };
};

const setInitialPassengers = (state, action) => {
  return {
    ...state,
    passengers: action.data,
  };
};

const addPassenger = (state, action) => {
  return {
    ...state,
    passengers: {
      ...state.passengers,
      [action.data]: state.passengers[action.data]
        ? state.passengers[action.data] + 1
        : 1,
    },
  };
};

const removePassenger = (state, action) => {
  return {
    ...state,
    passengers: {
      ...state.passengers,
      [action.data]: state.passengers[action.data] - 1,
    },
  };
};

const deleteActualFlightServices = (state: SearchState): SearchState => {
  return {
    ...state,
    actualFlightServices: [],
  };
};

const toggleServiceInfo = (state: SearchState): SearchState => {
  return { ...state, toggleServiceInfo: !state.toggleServiceInfo };
};

const searchSetHotelPassengers = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchHotels: {
      ...state.searchHotels,
      ...action.data,
    },
  };
};

const searchSetHotelPaxNationality = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchHotels: {
      ...state.searchHotels,
      paxNationality: action.data,
    },
  };
};

const flightSearchAddAirports = (
  state: SearchState,
  action: AnyAction
): SearchState => {
  return {
    ...state,
    searchFormAirports: { ...state.searchFormAirports, ...action.data.value },
  };
};

export default function reducer(
  state: SearchState = initState,
  action: AnyAction
): SearchState {
  switch (action.type) {
    case actionTypes.SEARCH_SET:
      return { ...state, [action.data.name]: action.data.value };
    case actionTypes.FLIGHT_SEARCH_SUCCESS:
      return flightSearchSuccess(state, action);
    case actionTypes.SEARCH_SET_FORM_AIRPORTS:
      return flightSearchAddAirports(state, action);
    case actionTypes.FLIGHT_SEARCH_FAIL:
      return flightSearchFail(state, action);
    case actionTypes.SEARCH_SET_TRANSPORT_COMPANIES:
      return searchSetTransportCompanies(state, action);
    case actionTypes.SEARCH_SET_SEARCH_FLIGHTS:
      return searchSetSearchFlights(state, action);
    case actionTypes.SEARCH_ADD_SEARCH_FLIGHT:
      return searchAddSearchFlight(state, action);
    case actionTypes.SEARCH_REMOVE_SEARCH_FLIGHT:
      return searchRemoveSearchFlight(state, action);
    case actionTypes.SEARCH_UPDATE_SEARCH_FLIGHT_ORIGIN:
      return searchUpdateSearchFlightOrigin(state, action);
    case actionTypes.SEARCH_UPDATE_SEARCH_FLIGHT_DESTINATION:
      return searchUpdateSearchFlightDestination(state, action);
    case actionTypes.SEARCH_UPDATE_SEARCH_FLIGHT_DEPARTURE_DATE:
      return searchUpdateSearchFlightDepartureDate(state, action);
    case actionTypes.SEARCH_UPDATE_SEARCH_FLIGHT_RETURN_DATE:
      return searchUpdateSearchFlightReturnDate(state, action);
    case actionTypes.SEARCH_SET_AIRPORTS:
      return searchSetAirports(state, action);
    case actionTypes.SEARCH_SET_ACTUAL_FLIGHT_SERVICES:
      return setActualFlightServices(state, action);
    case actionTypes.DELETE_ACTUAL_FLIGHT_SERVICES:
      return deleteActualFlightServices(state);
    case actionTypes.SEARCH_SET_AIRPLANES:
      return searchSetAirplanes(state, action);
    case actionTypes.SEARCH_SET_POLICIES:
      return searchSetPolicies(state, action);
    case actionTypes.SEARCH_SET_FILTERS_DATA:
      return searchSetAirportFilters(state, action);
    case actionTypes.FLIGHT_TOGGLE_SERVICE_INFO:
      return toggleServiceInfo(state);
    case "SEARCH_SET_HOTEL_PASSENGERS":
      return searchSetHotelPassengers(state, action);
    case "SEARCH_SET_HOTEL_PAX_NATIONALITY":
      return searchSetHotelPaxNationality(state, action);
    case actionTypes.SEARCH_SET_CLOSED_HOTEL_PROPERTY_ERROR:
      return {
        ...state,
        searchHotels: {
          ...state.searchHotels,
          closedHotelPropertyError: action.data,
        },
      };
    case actionTypes.SEARCH_SET_INITIAL_PASSENGERS:
      return setInitialPassengers(state, action);
    case actionTypes.SEARCH_ADD_PASSENGER:
      return addPassenger(state, action);
    case actionTypes.SEARCH_REMOVE_PASSENGER:
      return removePassenger(state, action);
    default:
      return state;
  }
}
